/**
 * Single Blog Post Page
 * Clean reading experience. Comment support.
 */

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { Container, Section, Spacer } from '../components/primitives';
import { PageLoader } from '../components/common/LoadingSpinner';
import Button from '../components/common/Button';
import { Input, Textarea } from '../components/common/Input';
import type { BlogPost, BlogComment } from '../types';

export default function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [comments, setComments] = useState<BlogComment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Comment form
  const [commentForm, setCommentForm] = useState({
    author_name: '',
    author_email: '',
    content: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [commentSuccess, setCommentSuccess] = useState(false);

  useEffect(() => {
    if (slug) {
      loadPost();
    }
  }, [slug]);

  async function loadPost() {
    setLoading(true);
    setError(null);
    try {
      const res = await api.getBlogPost(slug!);
      setPost(res.post);
      setComments(res.comments);
    } catch (err) {
      setError('Post not found');
    } finally {
      setLoading(false);
    }
  }

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const handleCommentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      await api.submitComment(slug!, commentForm);
      setCommentSuccess(true);
      setCommentForm({ author_name: '', author_email: '', content: '' });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit comment');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <PageLoader />;
  }

  if (error || !post) {
    return (
      <main>
        <Section className="pt-32">
          <Container narrow>
            <div className="text-center py-16">
              <h1 className="text-h2 font-medium text-text-primary">Post not found</h1>
              <p className="mt-4 text-text-secondary">The article you're looking for doesn't exist.</p>
              <Link to="/blog" className="mt-8 inline-block text-accent hover:underline">
                Back to blog
              </Link>
            </div>
          </Container>
        </Section>
      </main>
    );
  }

  return (
    <main>
      {/* Article Header */}
      <Section size="lg" className="pt-20 md:pt-32 pb-12">
        <Container narrow>
          <Link to="/blog" className="inline-flex items-center text-small text-text-tertiary hover:text-text-primary mb-8">
            <span className="mr-2">&larr;</span> Back to blog
          </Link>

          <div className="flex items-center gap-3 text-small text-text-tertiary mb-4">
            <time>{formatDate(post.published_at || post.created_at)}</time>
            {post.category && (
              <>
                <span>&middot;</span>
                <span>{post.category}</span>
              </>
            )}
            {post.author_name && (
              <>
                <span>&middot;</span>
                <span>By {post.author_name}</span>
              </>
            )}
          </div>

          <h1 className="text-h1 font-medium text-text-primary leading-tight">
            {post.title}
          </h1>

          {post.excerpt && (
            <p className="mt-6 text-lg text-text-secondary">
              {post.excerpt}
            </p>
          )}
        </Container>
      </Section>

      {/* Featured Image */}
      {post.featured_image_url && (
        <Section size="sm">
          <Container>
            <div className="aspect-[21/9] bg-surface-subtle overflow-hidden">
              <img
                src={post.featured_image_url}
                alt={post.title}
                className="w-full h-full object-cover"
              />
            </div>
          </Container>
        </Section>
      )}

      {/* Article Content */}
      <Section>
        <Container narrow>
          <div
            className="prose prose-lg max-w-none
              prose-headings:font-medium prose-headings:text-text-primary
              prose-p:text-text-secondary prose-p:leading-relaxed
              prose-a:text-accent prose-a:no-underline hover:prose-a:underline
              prose-strong:text-text-primary prose-strong:font-medium
              prose-ul:text-text-secondary prose-ol:text-text-secondary
              prose-blockquote:border-l-accent prose-blockquote:text-text-secondary
              prose-code:text-accent prose-code:bg-surface-subtle prose-code:px-1 prose-code:py-0.5 prose-code:rounded
              prose-pre:bg-surface-subtle prose-pre:border prose-pre:border-border-subtle"
            dangerouslySetInnerHTML={{
              __html: post.content_type === 'html' ? post.content : post.content
            }}
          />
        </Container>
      </Section>

      {/* Tags */}
      {post.tags && (
        <Section size="sm">
          <Container narrow>
            <div className="flex flex-wrap gap-2 pt-8 border-t border-border-subtle">
              {post.tags.split(',').map((tag) => (
                <span
                  key={tag.trim()}
                  className="px-3 py-1 text-small text-text-tertiary bg-surface-subtle"
                >
                  {tag.trim()}
                </span>
              ))}
            </div>
          </Container>
        </Section>
      )}

      {/* Comments Section */}
      {post.allow_comments === 1 && (
        <Section background="subtle">
          <Container narrow>
            <h2 className="text-h3 font-medium text-text-primary">
              Comments {comments.length > 0 && `(${comments.length})`}
            </h2>

            {/* Existing Comments */}
            {comments.length > 0 && (
              <div className="mt-8 space-y-6">
                {comments.map((comment) => (
                  <div key={comment.id} className="bg-white p-6 border border-border-subtle">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-small font-medium text-text-primary">
                        {comment.author_name}
                      </span>
                      <time className="text-caption text-text-tertiary">
                        {formatDate(comment.created_at)}
                      </time>
                    </div>
                    <p className="text-base text-text-secondary">{comment.content}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Comment Form */}
            <div className="mt-12 bg-white p-6 md:p-8 border border-border-subtle">
              <h3 className="text-h4 font-medium text-text-primary mb-6">Leave a comment</h3>

              {commentSuccess ? (
                <div className="p-4 bg-green-50 border border-green-200 text-green-700 text-small">
                  Thank you! Your comment has been submitted for moderation.
                </div>
              ) : (
                <form onSubmit={handleCommentSubmit} className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <Input
                      label="Name"
                      value={commentForm.author_name}
                      onChange={(e) => setCommentForm({ ...commentForm, author_name: e.target.value })}
                      required
                    />
                    <Input
                      label="Email"
                      type="email"
                      value={commentForm.author_email}
                      onChange={(e) => setCommentForm({ ...commentForm, author_email: e.target.value })}
                      required
                      hint="Your email won't be published"
                    />
                  </div>
                  <Textarea
                    label="Comment"
                    value={commentForm.content}
                    onChange={(e) => setCommentForm({ ...commentForm, content: e.target.value })}
                    rows={4}
                    required
                  />
                  {error && (
                    <p className="text-small text-red-600">{error}</p>
                  )}
                  <Button type="submit" isLoading={submitting}>
                    Submit Comment
                  </Button>
                </form>
              )}
            </div>
          </Container>
        </Section>
      )}

      <Spacer size="lg" />
    </main>
  );
}
